import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';
import { clearAuth, getToken } from '@/utils/auth';
import { isPublicRoutePath } from '@/utils/routes';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success?: boolean;
  code?: number;
  message?: string;
  data?: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

const loginPath = '/user/login';
const isBrowser = typeof window !== 'undefined';
let isAuthRedirecting = false;

const handleUnauthorized = (hint?: string) => {
  if (!isBrowser) {
    return;
  }
  const pathname = history.location.pathname;
  clearAuth();
  if (isAuthRedirecting) {
    return;
  }
  isAuthRedirecting = true;
  message.warning(hint || '请重新登录');
  if (pathname !== loginPath && !isPublicRoutePath(pathname)) {
    history.push(loginPath);
  }
  window.setTimeout(() => {
    isAuthRedirecting = false;
  }, 1500);
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {
        success,
        code,
        message,
        data,
        errorCode,
        errorMessage,
        showType,
      } = res as unknown as ResponseStructure;
      const shouldThrow =
        (typeof success === 'boolean' && !success) ||
        (typeof success === 'undefined' &&
          typeof code === 'number' &&
          code !== 0);
      if (shouldThrow) {
        const error: any = new Error(errorMessage || message);
        error.name = 'BizError';
        error.info = {
          errorCode: errorCode ?? code,
          errorMessage: errorMessage ?? message,
          showType,
          data,
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          if (errorCode === 401) {
            handleUnauthorized(errorMessage);
            return;
          }
          const errorText = errorMessage || '请求失败，请重试。';
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorText);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorText);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorText,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorText);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        if (error.response.status === 401) {
          handleUnauthorized();
          return;
        }
        message.error(`响应状态码：${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('请求未响应，请重试。');
      } else {
        // 发送请求时出了点问题
        message.error('请求发生错误，请重试。');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const token = getToken();
      if (!token) {
        return config;
      }
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        },
      };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as { data?: ResponseStructure };

      if (data?.code === 401 || data?.errorCode === 401) {
        handleUnauthorized(data?.message || data?.errorMessage);
        return response;
      }
      if (data?.success === false) {
        message.error(data?.errorMessage || '请求失败！');
      }
      if (typeof data?.code === 'number' && data.code !== 0) {
        message.error(data?.message || '请求失败！');
      }
      return response;
    },
    [
      (response) => response,
      (error: any) => {
        const status = error?.response?.status;
        if (status === 401) {
          handleUnauthorized();
        }
        return Promise.reject(error);
      },
    ],
  ],
};
